import { useEffect, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formErrors, revalidateLogic, useForm, useStore } from '@eureca/ui/forms';
import fetchClient from '@eureca/recruiter-api-sdk/client';
import {
  CompanyEditPage as CompanyEditPageComponent,
  type CompanyEditValues,
} from './CompanyEditPage.component';
import {
  companyByIdQueryOptions,
  CompanyDetailFetchError,
  type CompanyDetail,
} from './company-detail-query';
import { CompanyIntegrationsSection } from './CompanyIntegrationsSection';
import { formatCnpj } from '../company-create/cnpj';
import { companyFormSchema } from '../company-create/company-form.schema';
import { validateLogoFile, type LogoValidationError } from '../company-create/logo-upload';
import { DEFAULT_COMPANIES_SEARCH } from '../companies-list/companies-search.schema';

interface BackendErrorBody {
  error?: string;
}

class UpdateCompanyError extends Error {
  readonly name = 'UpdateCompanyError';
  constructor(
    readonly errorKey: string,
    readonly status: number
  ) {
    super(errorKey);
  }
}

/**
 * Edit page — loads the company via TanStack Query (Suspense boundary in the
 * route), seeds the form, PATCHes on submit. Slug field is rendered readonly
 * by the component (and dropped from the PATCH body — Core's PATCH schema
 * is `.strict()` and omits slug).
 */
export default function CompanyEditPage() {
  const navigate = useNavigate();
  const { companyId } = useParams({ from: '/_authenticated/companies/$companyId/edit' });
  const query = useQuery(companyByIdQueryOptions(companyId));

  if (query.isLoading || !query.data) {
    if (query.error instanceof CompanyDetailFetchError && query.error.status === 404) {
      return (
        <NotFoundFallback onBack={() => navigate({ to: '/', search: DEFAULT_COMPANIES_SEARCH })} />
      );
    }
    return <LoadingFallback />;
  }
  const company = query.data;

  return <CompanyEditFormShell companyId={companyId} company={company} navigate={navigate} />;
}

interface CompanyEditFormShellProps {
  companyId: string;
  company: CompanyDetail;
  navigate: ReturnType<typeof useNavigate>;
}

function CompanyEditFormShell({ companyId, company, navigate }: CompanyEditFormShellProps) {
  const queryClient = useQueryClient();
  // Two pieces of logo state:
  //   - `logoFile` — the freshly-picked File (null until the user replaces it),
  //     consumed by the multipart upload mutation on submit.
  //   - `logoFileName` — what the LogoFileInput renders. Starts as the current
  //     stored filename derived from the company's logoUrl; flips to the new
  //     filename when the user picks a replacement.
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoFileName, setLogoFileName] = useState<string | null>(
    deriveFilenameFromUrl(company.logoUrl)
  );
  // Avatar source for the title:
  //   - default: stored `logoUrl` (already public-readable via S3 + CDN)
  //   - when user picks a new file: blob URL of the picked File for instant
  //     preview, then revoked when the file changes again or the page unmounts
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(company.logoUrl);
  useEffect(() => {
    if (!logoFile) {
      setLogoPreviewUrl(company.logoUrl);
      return;
    }
    const url = URL.createObjectURL(logoFile);
    setLogoPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [logoFile, company.logoUrl]);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [duplicateField, setDuplicateField] = useState<'cnpj' | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Use the full create schema (slug included) — the loaded slug is already
  // valid by construction, and the field is readonly in this page so its
  // value never changes. The slug is stripped from the PATCH payload below
  // because Core's PATCH schema is `.strict()` and rejects unknown keys.
  const form = useForm({
    defaultValues: {
      name: company.name,
      cnpj: company.cnpj,
      slug: company.slug,
      website: company.websiteUrl,
      primaryColor: company.primaryColor,
    } satisfies CompanyEditValues,
    validationLogic: revalidateLogic(),
    validators: { onDynamic: companyFormSchema },
    onSubmit: async ({ value }) => {
      await submitPatch(value);
    },
  });

  const values = useStore(form.store, (state) => state.values);
  const isFormSubmitting = useStore(form.store, (state) => state.isSubmitting);
  const fieldErrors = formErrors(form);
  const errors: Record<string, string> = { ...fieldErrors };
  if (logoError) errors.logo = logoError;
  if (duplicateField === 'cnpj') errors.cnpj = 'CNPJ já cadastrado';

  const patchMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const res = await fetchClient<{ id: string; slug: string }>({
        method: 'PATCH',
        url: `/companies/${companyId}`,
        data: payload,
      });
      if (res.error) {
        const body = res.error as { status: number; error?: BackendErrorBody } | undefined;
        throw new UpdateCompanyError(body?.error?.error ?? 'update_failed', body?.status ?? 500);
      }
      return res.data;
    },
    onSuccess: () => {
      // Drop both cached list shapes (table `page` + cards `infinite`) and the
      // detail entry for this company so the listing/edit pages refetch the
      // updated row instead of showing stale name/cnpj/etc.
      void queryClient.invalidateQueries({ queryKey: ['recruiter', 'companies'] });
    },
  });

  /**
   * Logo replacement is a SEPARATE mutation because the metadata PATCH stays
   * JSON (slug-readonly form, no multipart machinery for the common
   * metadata-only edit). The BFF endpoint uploads the new logo, PATCHes Core
   * with the new logoUrl, and best-effort deletes the previous storage object.
   */
  const logoMutation = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('logo', file);
      const res = await fetchClient<{ id: string; slug: string }>({
        method: 'POST',
        url: `/companies/${companyId}/logo`,
        data: fd as unknown,
      });
      if (res.error) {
        const body = res.error as { status: number; error?: BackendErrorBody } | undefined;
        throw new UpdateCompanyError(body?.error?.error ?? 'update_failed', body?.status ?? 500);
      }
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['recruiter', 'companies'] });
    },
  });

  const isSubmitting = isFormSubmitting || patchMutation.isPending || logoMutation.isPending;

  async function submitPatch(value: CompanyEditValues) {
    setSubmitError(null);
    setDuplicateField(null);
    setLogoError(null);

    // Drop slug from PATCH payload — Core rejects unknown fields under `.strict()`.
    const payload = {
      name: value.name,
      cnpj: value.cnpj,
      websiteUrl: value.website,
      primaryColor: value.primaryColor,
    };

    try {
      // Sequenced: metadata first, then logo. If metadata fails (e.g. CNPJ
      // duplicate) we never touch storage; if metadata succeeds and logo
      // fails the user keeps the saved name/cnpj/etc. and can retry the
      // logo without re-entering the form.
      await patchMutation.mutateAsync(payload);
      if (logoFile) {
        await logoMutation.mutateAsync(logoFile);
      }
      void navigate({
        to: '/companies/$companyId/edit/success',
        params: { companyId },
      });
    } catch (err) {
      handleSubmitError(err);
    }
  }

  async function handleLogoChange(file: File | null) {
    setLogoError(null);
    if (!file) {
      setLogoFile(null);
      // Re-derive from the loaded company when the user clears the picker, so
      // the field reverts to "showing the currently saved logo's filename"
      // rather than going blank.
      setLogoFileName(deriveFilenameFromUrl(company.logoUrl));
      return;
    }
    const result = await validateLogoFile(file);
    if (!result.ok) {
      setLogoError(translateLogoError(result.error));
      setLogoFile(null);
      return;
    }
    setLogoFile(file);
    setLogoFileName(file.name);
  }

  function translateLogoError(err: LogoValidationError): string {
    return err.message;
  }

  function handleSubmitError(err: unknown) {
    if (!(err instanceof UpdateCompanyError)) {
      setSubmitError('Falha ao salvar as alterações. Tente novamente.');
      return;
    }
    if (err.errorKey === 'cnpj_duplicado') return setDuplicateField('cnpj');
    if (err.errorKey === 'logo_too_large') {
      setLogoError('O arquivo deve ter no máximo 5 MB');
      return;
    }
    if (err.errorKey === 'invalid_logo_type') {
      setLogoError('Envie um arquivo .JPG ou .PNG');
      return;
    }
    if (err.errorKey === 'logo_required') {
      setLogoError('Envie uma imagem para a logo da empresa');
      return;
    }
    if (err.status === 404) {
      setSubmitError('Empresa não encontrada.');
      return;
    }
    setSubmitError('Falha ao salvar as alterações. Tente novamente.');
  }

  return (
    <CompanyEditPageComponent
      status={company.status}
      values={values}
      errors={errors}
      logoFileName={logoFileName}
      logoPreviewUrl={logoPreviewUrl}
      isSubmitting={isSubmitting}
      submitError={submitError}
      slugReadonly
      onFieldChange={(field, value) => {
        if (field === 'cnpj') {
          setDuplicateField(null);
          form.setFieldValue(field, formatCnpj(value));
          return;
        }
        form.setFieldValue(field, value);
      }}
      onLogoChange={handleLogoChange}
      onSubmit={() => form.handleSubmit()}
    >
      <CompanyIntegrationsSection companyId={companyId} />
    </CompanyEditPageComponent>
  );
}

function deriveFilenameFromUrl(logoUrl: string | null): string | null {
  if (!logoUrl) return null;
  try {
    const path = new URL(logoUrl).pathname;
    const last = path.split('/').filter(Boolean).pop();
    return last ?? null;
  } catch {
    return null;
  }
}

function LoadingFallback() {
  return (
    <div
      className="flex min-h-dvh flex-col items-center justify-center bg-muted text-muted-foreground"
      role="status"
      aria-live="polite"
    >
      Carregando empresa…
    </div>
  );
}

function NotFoundFallback({ onBack }: { onBack: () => void }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-muted px-8 text-center">
      <h1 className="text-2xl font-medium text-foreground">Empresa não encontrada</h1>
      <p className="text-muted-foreground">
        A empresa que você tentou abrir não existe ou não está vinculada ao seu squad.
      </p>
      <button
        type="button"
        onClick={onBack}
        className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
      >
        Voltar para a listagem
      </button>
    </div>
  );
}
